import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import api from "@/lib/api";

import Landing from "@/pages/Landing";
import Bilan from "@/pages/Bilan";
import ClientHome from "@/pages/ClientHome";
import Profile from "@/pages/Profile";
import Program from "@/pages/Program";
import SessionRun from "@/pages/SessionRun";
import ClientResetPassword from "@/pages/ClientResetPassword";
import Evolution from "@/pages/Evolution";
import Measurements from "@/pages/Measurements";
import Booking from "@/pages/Booking";
import Pricing from "@/pages/Pricing";
import BookAndPay from "@/pages/BookAndPay";
import Chat from "@/pages/Chat";
import CoachPromo from "@/pages/coach/CoachPromo";
import PaymentSuccess from "@/pages/PaymentSuccess";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import CoachClient from "@/pages/coach/CoachClient";
import CoachMethod from "@/pages/coach/CoachMethod";
import CoachEngine from "@/pages/coach/CoachEngine";
import CoachMaiEngine from "@/pages/coach/CoachMaiEngine";
import CoachAgenda from "@/pages/coach/CoachAgenda";
import CoachLibrary from "@/pages/coach/CoachLibrary";
import CoachPricing from "@/pages/coach/CoachPricing";
import CoachWelcomeOffer from "@/pages/coach/CoachWelcomeOffer";
import CoachAdmin from "@/pages/coach/CoachAdmin";
import Referral from "@/pages/Referral";
import CoachReferral from "@/pages/coach/CoachReferral";
import Admin from "@/pages/Admin";
import CoachAdmins from "@/pages/coach/CoachAdmins";
import AdminAiCost from "@/pages/coach/AdminAiCost";
import CoachCanary from "@/pages/coach/CoachCanary";
import Support from "@/pages/Support";
import History from "@/pages/History";
import Decouverte from "@/pages/Decouverte";
import MaPrepa from "@/pages/MaPrepa";
import ComplementarySports from "@/pages/ComplementarySports";
import AddToHomeScreen from "@/components/AddToHomeScreen";
import UpdatePrompt from "@/components/UpdatePrompt";
import CoachChannel from "@/pages/CoachChannel";
import CoachMessages from "@/pages/coach/CoachMessages";
import CoachReviews from "@/pages/coach/CoachReviews";
import CoachRewards from "@/pages/coach/CoachRewards";
import Cgu from "@/pages/Cgu";
import Confidentialite from "@/pages/Confidentialite";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-sand">
      <div className="pulse-organic w-3 h-3 rounded-full bg-sage" />
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sid });
        const ref = localStorage.getItem("ref_code");
        if (ref) { try { await api.post("/referral/attach", { code: ref }); } catch {} localStorage.removeItem("ref_code"); }
        setUser(res.data);
        window.history.replaceState(null, "", "/");
        navigate(res.data.role === "coach" ? "/coach" : "/app", { replace: true });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <Loader />;
}

function Protected({ children, staff = false, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  const isAdmin = user.role === "admin";
  const isCoach = user.role === "coach";
  if (adminOnly && !isAdmin) return <Navigate to="/coach" replace />;
  if (staff && !(isAdmin || isCoach)) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/decouverte" element={<Decouverte />} />
      <Route path="/ma-prepa" element={<MaPrepa />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/support" element={<Support />} />
      <Route path="/coach/admin" element={<Protected adminOnly><CoachAdmin /></Protected>} />
      <Route path="/coach/admins" element={<Protected adminOnly><CoachAdmins /></Protected>} />
      <Route path="/coach/cout-ia" element={<Protected adminOnly><AdminAiCost /></Protected>} />
      <Route path="/coach/canary" element={<Protected adminOnly><CoachCanary /></Protected>} />
      <Route path="/bilan" element={<Protected><Bilan /></Protected>} />
      <Route path="/app" element={<Protected><ClientHome /></Protected>} />
      <Route path="/reinitialiser" element={<ClientResetPassword />} />
      <Route path="/profil" element={<Protected><Profile /></Protected>} />
      <Route path="/programme" element={<Protected><Program /></Protected>} />
      <Route path="/seance/:id" element={<Protected><SessionRun /></Protected>} />
      <Route path="/evolution" element={<Protected><Evolution /></Protected>} />
      <Route path="/mensurations" element={<Protected><Measurements /></Protected>} />
      <Route path="/historique" element={<Protected><History /></Protected>} />
      <Route path="/sports-complementaires" element={<Protected><ComplementarySports /></Protected>} />
      <Route path="/mon-coach" element={<Protected><CoachChannel /></Protected>} />
      <Route path="/reservation" element={<Protected><Booking /></Protected>} />
      <Route path="/tarifs" element={<Protected><Pricing /></Protected>} />
      <Route path="/reserver" element={<Protected><BookAndPay /></Protected>} />
      <Route path="/assistant" element={<Protected><Chat /></Protected>} />
      <Route path="/parrainage" element={<Protected><Referral /></Protected>} />
      <Route path="/paiement/succes" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/coach" element={<Protected staff><CoachDashboard /></Protected>} />
      <Route path="/coach/client/:id" element={<Protected staff><CoachClient /></Protected>} />
      <Route path="/coach/agenda" element={<Protected staff><CoachAgenda /></Protected>} />
      <Route path="/coach/messages" element={<Protected staff><CoachMessages /></Protected>} />
      <Route path="/coach/avis" element={<Protected staff><CoachReviews /></Protected>} />
      <Route path="/coach/recompenses" element={<Protected staff><CoachRewards /></Protected>} />
      <Route path="/coach/methode" element={<Protected adminOnly><CoachMethod /></Protected>} />
      <Route path="/coach/promos" element={<Protected adminOnly><CoachPromo /></Protected>} />
      <Route path="/coach/moteur" element={<Protected adminOnly><CoachEngine /></Protected>} />
      <Route path="/coach/moteur-mai" element={<Protected adminOnly><CoachMaiEngine /></Protected>} />
      <Route path="/coach/bibliotheque" element={<Protected adminOnly><CoachLibrary /></Protected>} />
      <Route path="/coach/tarifs" element={<Protected adminOnly><CoachPricing /></Protected>} />
      <Route path="/coach/offre-bienvenue" element={<Protected adminOnly><CoachWelcomeOffer /></Protected>} />
      <Route path="/coach/parrainage" element={<Protected adminOnly><CoachReferral /></Protected>} />
      <Route path="/cgu" element={<Cgu />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <AppRoutes />;
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="light" toastOptions={{ style: { fontFamily: "Manrope", borderRadius: 8 } }} />
          <Router />
          <AddToHomeScreen />
          <UpdatePrompt />
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
